<template>
  <div class="content-wrapper">
    <h2 class="page-titles">Order History</h2>

    <!-- Export button here -->
    <transition name="fade">
      <div class="export-csv" v-show="loadStatus">
        <div class="mr-auto">
          <SearchAndFilter
            :placeholder="'Search by email or name'"
            :searchOnSubmit="true"
            :submitOnEnter="true"
          />
        </div>

        <button
          v-show="exportData.data.length"
          @click="handleExport"
          class="button-gemnote button-gemnote--small"
        >
          <span> Export {{ selectedTab }}</span>
        </button>
      </div>
    </transition>

    <ExportModal
      :showFooter="true"
      v-show="isExportModalActive"
      @hideModal="closeModal"
      :showCancelButton="false"
      @confirmAction="closeModal"
      colorVariation="coast"
      width="600px"
    >
      <template slot="header">
        <span>Exporting Orders</span>
      </template>

      <template slot="body">
        <span
          >Your orders report is now being generated and you will receive a
          download link at <strong> {{ user.email }} </strong> when it's ready.
          Keep an eye on your inbox!
        </span>
      </template>
    </ExportModal>

    <div class="margin-top-4" v-if="isTeamOwner">
      <Tabs :tabs="adminTabs" @setActiveTab="setActiveTab($event)" />
      <transition name="slide-fade">
        <MyOrderList
          v-if="adminTabs[0].isActive"
          @subscribed="setActiveTab($event)"
        />
        <MyTeamOrderList v-if="adminTabs[1].isActive" />
        <RedemptionOrderList v-if="adminTabs[2].isActive" />
      </transition>
    </div>

    <div v-else>
      <Tabs :tabs="userTabs" @setActiveTab="setActiveTab($event)" />
      <MyOrderList
        v-if="userTabs[0].isActive"
        @subscribed="setActiveTab($event)"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { storeModules, progressStatus } from '../helpers'
import SearchAndFilter from '@/components/Universal/SearchAndFilter'
import moment from 'moment'
import store from '../store'

export default {
  name: 'Orders',
  beforeRouteEnter(routeTo, routeFrom, next) {
    if (
      store.state.team.team.teamModuleNames.includes(storeModules.swag_store)
    ) {
      next()
    } else if (
      store.state.team.team.teamModuleNames.includes(storeModules.project)
    ) {
      next({ name: 'Projects' })
    }
  },

  components: {
    ExportModal: () => import('@/components/Universal/ExportModal'),
    Tabs: () => import('../components/Universal/Tabs'),
    SearchAndFilter,
    MyOrderList: () => import('@/components/Orders/MyOrderList'),
    MyTeamOrderList: () => import('@/components/Orders/MyTeamOrderList'),
    RedemptionOrderList: () => import('@/components/Orders/RedemptionOrderList')
  },

  data() {
    return {
      adminTabs: [
        { name: 'My Orders', value: 'myOrders', isActive: true },
        { name: 'Team Orders', value: 'teamOrders', isActive: false },
        { name: 'Redemption Orders', value: 'groupOrders', isActive: false }
      ],
      userTabs: [{ name: 'My Orders', isActive: true }],

      file_fields: [
        'createdAt',
        'id',
        'state',
        'deliveryName',
        'deliveryPhone',
        'deliveryEmail',
        'deliveryCountry',
        'deliveryCity',
        'deliveryState',
        'deliveryAddressOne',
        'deliveryAddressTwo',
        'deliveryZipcode',
        'itemsOrdered',
        'total',
        'trackingNumber',
        'trackingUrl'
      ],

      file_labels: {
        createdAt: 'Date',
        id: 'Order #',
        state: 'Ship Status',
        deliveryName: 'Name',
        deliveryPhone: 'Phone',
        deliveryEmail: 'Email',
        deliveryCountry: 'Country',
        deliveryCity: 'City',
        deliveryState: 'State',
        deliveryAddressOne: 'Address_1',
        deliveryAddressTwo: 'Address_2',
        deliveryZipcode: 'Zipcode',
        itemsOrdered: 'Items Ordered',
        total: 'Total',
        trackingNumber: 'Tracking Number',
        trackingUrl: 'Tracking Url'
      },
      teamOrders: [],
      redemptionOrders: [],
      selectedTab: 'My Orders'
    }
  },

  computed: {
    ...mapGetters({ isTeamOwner: 'profile/isTeamOwner' }),
    ...mapState({
      user: state => state.profile.user,
      allTeamOrders: state => state.order.allTeamOrders,
      ordersListLoading: state => state.order.ordersListLoading,
      currentOrdersList: state => state.order.currentOrdersList,
      isExportModalActive: state => state.modal.isExportModalActive
    }),

    loadStatus() {
      return progressStatus(this.ordersListLoading)
    },

    exportData() {
      if (this.adminTabs[0].isActive) {
        return {
          data: this.mappedData(this.currentOrdersList),
          text: 'My Orders',
          size: 'small'
        }
      } else if (this.adminTabs[1].isActive) {
        return {
          data: this.mappedData(this.currentOrdersList),
          text: 'Team Orders',
          size: 'small'
        }
      } else {
        return {
          data: this.mappedData(this.allTeamOrders),
          text: 'Redemption Orders',
          size: 'small'
        }
      }
    }
  },
  watch: {
    adminTabs: {
      handler() {
        const isChecked = this.adminTabs.filter(tabs => {
          return tabs.isActive === true
        })
        this.selectedTab = isChecked[0].name
      },
      deep: true
    }
  },
  mounted() {
    let value = this.$route.hash ? this.$route.hash : this.$route.params.tab
    if (!value) {
      return
    }
    value = value
      .replace(/[^a-zA-Z ]/g, '')
      .replace(/([A-Z])/g, ' $1')
      .trim()
    this.setActiveTab({ name: value })
  },

  methods: {
    mappedData: array => {
      const returnData = array.map(order => ({
        createdAt: moment(order.createdAt).format(' dddd, MMMM DD, YYYY '),
        id: order.id.split('-')[0].toUpperCase(),
        state: order.state,
        deliveryName: order.deliveryName,
        deliveryPhone: order.deliveryPhone,
        deliveryEmail: order.deliveryEmail,
        deliveryCountry: order.deliveryCountry,
        deliveryCity: order.deliveryCity,
        deliveryState: order.deliveryState,
        deliveryAddressOne: order.deliveryAddressOne,
        deliveryAddressTwo: order.deliveryAddressTwo,
        deliveryZipcode: order.deliveryZipcode,
        itemsOrdered: order.orderItems
          ? order.orderItems.map(item => {
              return (
                '\n' +
                item.quantity +
                ' ' +
                'qty' +
                ' ' +
                'x' +
                ' ' +
                item.displayName
              )
            })
          : '',
        total: '$' + order.total,

        trackingNumber:
          order.trackingNumber == null ? '-' : order.trackingNumber,

        trackingUrl: order.trackingUrl == null ? '-' : order.trackingUrl
      }))
      return returnData
    },
    setActiveTab(selectedTab) {
      this.$router.push({ name: 'orders', hash: `#${selectedTab.name}` })
      if (this.isTeamOwner) {
        this.adminTabs.forEach(tab => {
          tab.isActive = tab.name === selectedTab.name
        })
      }
    },

    handleExport() {
      const isChecked = this.adminTabs.filter(tabs => {
        return tabs.isActive === true
      })
      let route
      if (isChecked[0].name === 'My Orders') {
        route = 'personal_orders'
      } else if (isChecked[0].name === 'Team Orders') {
        route = 'team_orders'
      } else if (isChecked[0].name === 'Redemption Orders') {
        route = 'redemption_orders'
      }
      this.$store.dispatch('modal/handleExports', route)
    },

    closeModal() {
      this.$store.dispatch('modal/setExportModal', false)
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/global.scss';

.export-csv {
  display: flex;
  align-items: center;
  margin: 1rem 0;
  padding-top: 1.25rem;

  .mr-auto {
    width: 30%;
    margin-right: auto;
  }
}
.button-gemnote {
  background: $main-white;
  color: $main-6;
  width: auto;
  padding: 0 1.25rem;
  height: 2.5rem;

  &:hover {
    background: $main-white;
    color: $main-6;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}

.slide-fade-enter-active {
  transition: all 1s ease;
}
.slide-fade-leave-active {
  transition: all 0 cubic-bezier(1, 0.5, 0.8, 1);
}
.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateX(10px);
  opacity: 0.5;
}
</style>
